import React, { useState, useEffect, useCallback } from 'react';
import { FileText, Send, Download, X, RefreshCw, Sparkles, AlertTriangle } from 'lucide-react';
import { Button } from './ui/button';
import { LoadingSpinner } from './UIComponents';
import { reportingAPI } from '../utils/api';
import { toast } from 'sonner';
import SiteReportingConfigCard from './SiteReportingConfigCard';

const FORMAT_OPTIONS = [
  { value: '', label: 'Auto-suggest' },
  { value: 'daily', label: 'Daily-style (hourly chart)' },
  { value: 'weekly', label: 'Weekly-style (daily chart)' },
  { value: 'monthly', label: 'Monthly-style (weekly chart + narrative)' },
];

function todayStr(offsetDays = 0) {
  const d = new Date();
  d.setDate(d.getDate() + offsetDays);
  return d.toISOString().slice(0, 10);
}

export default function ReportsTab({ siteId, siteName, onSiteUpdated }) {
  const [from, setFrom] = useState(todayStr(-7));
  const [to, setTo] = useState(todayStr());
  const [format, setFormat] = useState('');
  const [includeNarrative, setIncludeNarrative] = useState(false);
  const [recipients, setRecipients] = useState('');
  const [generating, setGenerating] = useState(false);
  const [lastReport, setLastReport] = useState(null);
  const [sending, setSending] = useState(false);

  const [history, setHistory] = useState([]);
  const [loadingHistory, setLoadingHistory] = useState(true);

  const fetchHistory = useCallback(async () => {
    try {
      const res = await reportingAPI.listReports(siteId);
      setHistory(res.data?.reports || []);
    } catch (err) {
      toast.error('Failed to load report history');
    } finally {
      setLoadingHistory(false);
    }
  }, [siteId]);

  useEffect(() => { fetchHistory(); }, [fetchHistory]);

  const handleGenerate = async () => {
    if (!from || !to) return;
    setGenerating(true);
    setLastReport(null);
    try {
      const res = await reportingAPI.generateCustom(siteId, {
        period_start: from,
        period_end: to,
        format: format || null,
        include_narrative: includeNarrative,
      });
      setLastReport(res.data);
      await reportingAPI.openPreview(res.data.report_id);
      toast.success(`Report generated (${res.data.report_format} style) — opened in a new tab`);
      fetchHistory();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Report generation failed');
    } finally {
      setGenerating(false);
    }
  };

  const handleSendEmail = async (reportId, presetRecipients) => {
    const list = (presetRecipients || recipients)
      .split(',').map((s) => s.trim()).filter(Boolean);
    if (list.length === 0) {
      toast.error('Enter at least one recipient email');
      return;
    }
    if (lastReport?.report_id === reportId && lastReport?.validation && !lastReport.validation.passed) {
      toast.warning('Sending despite failed validation checks — this is a manual override.');
    }
    setSending(true);
    try {
      await reportingAPI.sendEmail(reportId, { recipients: list });
      toast.success(`Report emailed to ${list.join(', ')}`);
      fetchHistory();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Send failed');
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="space-y-6" data-testid="reports-tab">
      <SiteReportingConfigCard siteId={siteId} onSaved={onSiteUpdated} />

      {/* Generate custom report */}
      <div className="bg-white border border-slate-200 rounded-sm p-6" data-testid="generate-custom-report-card">
        <div className="flex items-center space-x-2 mb-4">
          <FileText className="w-5 h-5 text-blue-600" />
          <h3 className="font-semibold text-slate-900">Generate Custom Report</h3>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-4">
          <div>
            <label className="text-[10px] text-slate-500 uppercase block mb-1">From</label>
            <input type="date" value={from} onChange={(e) => setFrom(e.target.value)}
              className="w-full text-sm border border-slate-200 rounded px-2 py-1.5" data-testid="report-from-date" />
          </div>
          <div>
            <label className="text-[10px] text-slate-500 uppercase block mb-1">To</label>
            <input type="date" value={to} onChange={(e) => setTo(e.target.value)}
              className="w-full text-sm border border-slate-200 rounded px-2 py-1.5" data-testid="report-to-date" />
          </div>
          <div className="col-span-2">
            <label className="text-[10px] text-slate-500 uppercase block mb-1">Report format</label>
            <select value={format} onChange={(e) => setFormat(e.target.value)}
              className="w-full text-sm border border-slate-200 rounded px-2 py-1.5" data-testid="report-format-select">
              {FORMAT_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
            </select>
          </div>
        </div>

        <div className="flex items-center space-x-2 mb-4">
          <input type="checkbox" id="narrative-toggle" checked={includeNarrative}
            onChange={(e) => setIncludeNarrative(e.target.checked)}
            className="w-4 h-4" data-testid="include-narrative-checkbox" />
          <label htmlFor="narrative-toggle" className="text-sm text-slate-700 flex items-center">
            <Sparkles className="w-3.5 h-3.5 mr-1 text-violet-500" />
            Include AI trend narrative (best for longer/monthly-style ranges)
          </label>
        </div>

        <div className="flex items-end gap-3">
          <div className="flex-1">
            <label className="text-[10px] text-slate-500 uppercase block mb-1">Recipients (optional, comma-separated)</label>
            <input type="text" value={recipients} onChange={(e) => setRecipients(e.target.value)}
              placeholder="ops@voltide.energy, client@company.com"
              className="w-full text-sm border border-slate-200 rounded px-2 py-1.5" data-testid="report-recipients-input" />
          </div>
          <Button onClick={handleGenerate} disabled={generating} data-testid="generate-preview-btn">
            {generating ? <RefreshCw className="w-4 h-4 mr-2 animate-spin" /> : <FileText className="w-4 h-4 mr-2" />}
            {generating ? 'Generating…' : 'Generate & Preview'}
          </Button>
        </div>

        {lastReport && (
          <div className="mt-4 flex items-center justify-between bg-blue-50 border border-blue-200 rounded-sm p-3" data-testid="last-generated-report-actions">
            <div className="text-sm text-blue-900">
              Generated: <strong>{lastReport.report_format}</strong>-style report for {lastReport.period_label}
            </div>
            <div className="flex items-center space-x-2">
              <Button size="sm" variant="outline" onClick={() => reportingAPI.openPreview(lastReport.report_id)} data-testid="preview-report-btn">
                <FileText className="w-3.5 h-3.5 mr-1.5" /> Preview
              </Button>
              <Button size="sm" variant="outline" onClick={() => reportingAPI.openPdf(lastReport.report_id)} data-testid="download-report-btn">
                <Download className="w-3.5 h-3.5 mr-1.5" /> Download PDF
              </Button>
              <Button size="sm" disabled={sending} onClick={() => handleSendEmail(lastReport.report_id)} data-testid="send-report-email-btn">
                <Send className="w-3.5 h-3.5 mr-1.5" /> Send Email
              </Button>
              <Button size="sm" variant="ghost" onClick={() => setLastReport(null)} data-testid="discard-report-btn">
                <X className="w-3.5 h-3.5" />
              </Button>
            </div>
          </div>
        )}

        {lastReport && lastReport.validation && !lastReport.validation.passed && (
          <div className="mt-3 flex items-start space-x-2 bg-amber-50 border border-amber-200 rounded-sm p-3" data-testid="validation-warning-banner">
            <AlertTriangle className="w-4 h-4 text-amber-600 shrink-0 mt-0.5" />
            <div className="text-sm text-amber-900">
              <strong>Needs review before sending:</strong> {lastReport.validation.failed_checks.join('; ')}.
              Sending will require a manual override.
            </div>
          </div>
        )}
      </div>

      {/* Report history */}
      <div className="bg-white border border-slate-200 rounded-sm overflow-hidden" data-testid="report-history-table">
        <div className="px-5 py-3 border-b border-slate-200">
          <h3 className="text-sm font-semibold text-slate-900">Report History</h3>
        </div>
        {loadingHistory ? (
          <LoadingSpinner />
        ) : history.length === 0 ? (
          <div className="p-8 text-center text-sm text-slate-500" data-testid="no-reports-state">
            No reports generated for {siteName} yet.
          </div>
        ) : (
          <table className="w-full text-sm">
            <thead className="bg-slate-50 border-b border-slate-200">
              <tr>
                <th className="text-left px-4 py-2 text-[11px] font-medium text-slate-500 uppercase">Type</th>
                <th className="text-left px-4 py-2 text-[11px] font-medium text-slate-500 uppercase">Period</th>
                <th className="text-left px-4 py-2 text-[11px] font-medium text-slate-500 uppercase">Sent To</th>
                <th className="text-left px-4 py-2 text-[11px] font-medium text-slate-500 uppercase">Status</th>
                <th className="text-right px-4 py-2 text-[11px] font-medium text-slate-500 uppercase">Actions</th>
              </tr>
            </thead>
            <tbody>
              {history.map((r) => (
                <tr key={r.report_id} className="border-b border-slate-100 last:border-0 hover:bg-slate-50" data-testid={`report-row-${r.report_id}`}>
                  <td className="px-4 py-2.5">
                    <span className="capitalize">{r.report_type}</span>
                    {r.generation_type === 'custom' && (
                      <span className="ml-2 text-[10px] font-bold bg-amber-100 text-amber-700 px-1.5 py-0.5 rounded">CUSTOM</span>
                    )}
                  </td>
                  <td className="px-4 py-2.5 text-[11px] text-slate-600">{r.period_label}</td>
                  <td className="px-4 py-2.5 text-[11px] text-slate-600">{(r.sent_to || []).join(', ') || '—'}</td>
                  <td className="px-4 py-2.5">
                    <span className={`inline-block px-2 py-0.5 rounded-sm text-[10px] font-medium ${
                      r.delivery_status === 'sent' ? 'bg-emerald-100 text-emerald-700'
                        : r.delivery_status === 'failed' ? 'bg-rose-100 text-rose-700'
                        : 'bg-slate-100 text-slate-700'
                    }`}>
                      {r.delivery_status}
                    </span>
                  </td>
                  <td className="px-4 py-2.5 text-right">
                    <Button size="sm" variant="outline" onClick={() => reportingAPI.openPdf(r.report_id)} data-testid={`view-pdf-${r.report_id}`}>
                      View PDF
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}
